<template>
  <div
    class="card border-0 settings-card"
    :class="isDarkMode ? 'settings-card-dark' : 'settings-card-light'"
  >
    <div class="settings-inner">
      <h5 class="settings-title mb-1">Nastavení</h5>
      <p class="settings-subtitle mb-3">Kliknutím se hned uloží</p>

      <div v-if="loading" class="settings-empty">Načítám nastavení…</div>
      <div v-else-if="!settings.length" class="settings-empty">Zatím tu žádné volby nejsou.</div>

      <div v-else class="d-flex flex-column gap-3">
        <div v-for="item in settings" :key="item.UserSettingTypeID" class="setting-row">
          <div class="setting-head">
            <div class="min-w-0">
              <div class="setting-name">{{ item.UserSettingTypeName }}</div>
              <div v-if="item.UserSettingTypeDesc" class="setting-desc">
                <template v-for="(part, idx) in descParts(item.UserSettingTypeDesc)" :key="idx">
                  <button
                    v-if="part.link"
                    type="button"
                    class="setting-desc-link"
                    @click.stop="emit('open-topup')"
                  >{{ part.text }}</button>
                  <template v-else>{{ part.text }}</template>
                </template>
              </div>
            </div>
            <div v-if="item.UserSettingTypeYN === 'Y'" class="form-check form-switch mb-0 setting-switch">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="item.UserSettingValue === 'Y'"
                :disabled="savingId === item.UserSettingTypeID"
                @change="toggleYn(item, $event)"
              >
            </div>
          </div>

          <div v-if="item.UserSettingTypeYN !== 'Y'" class="setting-pills">
            <button
              v-for="option in item.Values"
              :key="option.UserSettingTypeValueID"
              type="button"
              class="setting-pill"
              :class="{ 'is-active': String(item.UserSettingValue) === String(option.UserSettingTypeValueID) }"
              :disabled="savingId === item.UserSettingTypeID"
              @click="setChoice(item, option)"
            >{{ option.UserSettingTypeValue }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import dataService from "@/services/dataService";

const emit = defineEmits(["open-topup"]);
const store = useStore();
const swal = inject("$swal");

const descParts = (text) => {
  const src = String(text || "");
  const idx = src.toLowerCase().lastIndexOf("zde");
  if (idx < 0) return [{ text: src }];
  return [
    { text: src.slice(0, idx) },
    { link: true, text: src.slice(idx, idx + 3) },
    { text: src.slice(idx + 3) }
  ];
};
const isDarkMode = computed(() => !!store.state.isDarkMode);
const userID = computed(() => store.getters.getUser?.UserID);
const sessionHash = computed(() => store.getters.isLoggedIn);

const settings = ref([]);
const loading = ref(false);
const savingId = ref(null);

const isDarkSetting = (item) => String(item?.UserSettingType || "").toLowerCase() === "dark mode";

const applyDarkMode = (enabled) => {
  store.commit("setDarkMode", enabled);
};

watch(
  () => store.state.isDarkMode,
  (dark) => {
    const item = settings.value.find(isDarkSetting);
    if (item) item.UserSettingValue = dark ? "Y" : "N";
  }
);

const loadSettings = async () => {
  if (!userID.value || !sessionHash.value) return;
  loading.value = true;
  try {
    const response = await dataService.GetUserSetting(userID.value, sessionHash.value);
    settings.value = Array.isArray(response?.Data) ? response.Data : [];
    const dark = settings.value.find(isDarkSetting);
    if (dark) {
      if (dark.UserSettingTypeID) store.commit("setDarkSettingTypeId", dark.UserSettingTypeID);
      const raw = String(dark.UserSettingValue ?? "").trim().toUpperCase();
      if (raw === "Y" || raw === "TRUE" || raw === "1") applyDarkMode(true);
      else if (raw === "N" || raw === "FALSE" || raw === "0") applyDarkMode(false);
    }
  } finally {
    loading.value = false;
  }
};

const setSettingValue = (item, value) => {
  item.UserSettingValue = value;
};

const setSavingId = (value) => {
  savingId.value = value;
};

const saveSetting = async (item, payload) => {
  setSavingId(item.UserSettingTypeID);
  const previous = item.UserSettingValue;
  try {
    const response = await dataService.SetUserSetting(userID.value, sessionHash.value, {
      UserSettingTypeID: item.UserSettingTypeID,
      ...payload
    });
    if (!response?.Result) {
      setSettingValue(item, previous);
      swal.fire({
        position: "center",
        icon: "error",
        html: `${(response?.ErrorMessage || ["Nastavení se nepodařilo uložit"]).map((message) => `<h5>${message}</h5>`).join("")}`,
        showConfirmButton: false,
        timer: 3000
      });
      return false;
    }
    if (response.Data?.UserSettingValue != null) {
      setSettingValue(item, response.Data.UserSettingValue);
    }
    return true;
  } catch (error) {
    setSettingValue(item, previous);
    console.error(error);
    return false;
  } finally {
    setSavingId(null);
  }
};

const toggleYn = async (item, event) => {
  const next = event.target.checked ? "Y" : "N";
  if (isDarkSetting(item)) {
    setSavingId(item.UserSettingTypeID);
    const checkbox = event.target;
    await store.dispatch("setThemeMode", next === "Y");
    const row = settings.value.find(isDarkSetting);
    const enabled = !!store.state.isDarkMode;
    if (row) row.UserSettingValue = enabled ? "Y" : "N";
    checkbox.checked = enabled;
    setSavingId(null);
    return;
  }
  item.UserSettingValue = next;
  await saveSetting(item, { UserSettingValue: next });
};

const setChoice = async (item, option) => {
  if (String(item.UserSettingValue) === String(option.UserSettingTypeValueID)) return;
  item.UserSettingValue = String(option.UserSettingTypeValueID);
  await saveSetting(item, { UserSettingTypeValueID: option.UserSettingTypeValueID });
};

onMounted(loadSettings);
</script>

<style scoped>
.settings-card-light {
  background: #ffffff;
  border: 1px solid #E2E8F0 !important;
  border-radius: 0.85rem !important;
}

.settings-card-dark {
  background: #0F172A !important;
  border: 1px solid #334155 !important;
  border-radius: 0.85rem !important;
  box-shadow: none !important;
}

.settings-inner {
  padding: 1.15rem;
}

.settings-title {
  font-weight: 800;
  color: #0F172A;
}

.settings-card-dark .settings-title {
  color: #E2E8F0;
}

.settings-subtitle {
  font-size: 0.78rem;
  color: #94A3B8;
  margin: 0;
}

.settings-empty {
  color: #94A3B8;
  font-size: 0.85rem;
}

.setting-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: 0.55rem;
  padding-top: 0.75rem;
  border-top: 1px solid #E2E8F0;
}

.settings-card-dark .setting-row {
  border-top-color: #334155;
}

.setting-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 0.75rem;
  width: 100%;
}

.setting-name {
  font-weight: 700;
  font-size: 0.88rem;
  color: #0F172A;
}

.setting-switch {
  justify-self: end;
  padding-left: 0 !important;
  margin: 0;
  min-height: auto;
  display: flex;
  align-items: center;
}

.settings-card-dark .setting-name {
  color: #E2E8F0;
}

.setting-desc {
  font-size: 0.75rem;
  color: #64748B;
  line-height: 1.4;
}

.setting-desc-link {
  border: 0;
  padding: 0;
  background: none;
  color: #EA580C;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 0.15em;
  cursor: pointer;
}

.settings-card-dark .setting-desc-link {
  color: #FF8A4C;
}

.settings-card-dark .setting-desc {
  color: #94A3B8;
}

.setting-switch .form-check-input {
  width: 2.4rem;
  height: 1.25rem;
  margin: 0 !important;
  float: none;
  cursor: pointer;
  background-color: #CBD5E1;
  border: none;
}

.setting-switch .form-check-input:checked {
  background-color: #FF8A4C;
}

.setting-switch .form-check-input::after {
  top: 50%;
  transform: translate(2px, -50%);
}

.setting-switch .form-check-input:checked::after {
  transform: translate(21px, -50%);
}

.setting-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.2rem;
  width: max-content;
  max-width: 100%;
  border-radius: 999px;
  background: #F1F5F9;
}

.settings-card-dark .setting-pills {
  background: #1E293B;
}

.setting-pill {
  border: 0;
  background: transparent;
  color: #64748B;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  cursor: pointer;
}

.setting-pill.is-active {
  background: #ffffff;
  color: #0F172A;
}

.settings-card-dark .setting-pill {
  color: #94A3B8;
}

.settings-card-dark .setting-pill.is-active {
  background: #334155;
  color: #E2E8F0;
}

.setting-pill:disabled,
.setting-switch .form-check-input:disabled {
  opacity: 0.6;
  cursor: wait;
}
</style>
